import { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { getGoogleAddressFields } from '../../shared/services/SharedService';
import './StreetAddressModal.css';
import { Tooltip } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import InfoIcon from "@material-ui/icons/Info";

const CustomTooltip = withStyles({
  tooltip: {
    color: "black",
    backgroundColor: "#f7f7f7",
    border: "1px solid #E6E8ED",
    height: "auto",
    pointerEvents: "auto!important",
    maxHeight: "500px",
    maxWidth: "800px",
    overflowY: "none",
  },
  arrow: {
    color: "white",
    "&:before": {
      border: "2px solid #E6E8ED",
    },
  },
})(Tooltip);

const StreetAddressModal = ({ 
  show, 
  onHide, 
  onAddressSelect,
  phaseStreet,
  phaseCity,
  phaseState, 
  phaseZip,
  phaseIndex = 0
}) => {
  const [selectedStreet, setSelectedStreet] = useState(phaseStreet || '');
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [isValidAddress, setIsValidAddress] = useState(false);
  const streetInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setSelectedAddress(null);
      setSelectedStreet(phaseStreet?.trim() || '');      

      // If there's already a phase street, consider it valid
      if (phaseStreet?.trim()) {
        setValidationError('');
        setIsValidAddress(true);
      } else {
        setValidationError('Street address is required.');
        setIsValidAddress(false);
      }

      // Focus the input when modal opens
      setTimeout(() => {
        setSelectedStreet(phaseStreet || '');
        // if (streetInputRef.current) {
        //   streetInputRef.current.focus();
        // }
      }, 100);
    } else {
      // Clean up autocomplete when modal closes
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    }
  }, [show, streetInputRef.current, autocompleteRef.current]);

  // Initialize Google Places Autocomplete when modal is shown and Google Maps is available
  useEffect(() => {
    if (show && streetInputRef.current && window.google?.maps?.places && phaseCity && phaseState) {
      initializeAutocomplete();
    }
  }, [show, streetInputRef.current, phaseCity, phaseState]);

  const initializeAutocomplete = () => {
    if (!streetInputRef.current || !window.google?.maps?.places) {
      return;
    }

    try {
      // Clean up previous autocomplete instance
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }

      const autocomplete = new window.google.maps.places.Autocomplete(streetInputRef.current, {
        componentRestrictions: { country: 'US' },
        fields: ["address_components", "geometry"],
        types: ['address']
      });

      autocompleteRef.current = autocomplete;

      // Set geographic bounds for local suggestions
      setGeographicBounds(autocomplete);

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place && place.address_components) {
          handlePlaceSelection(place);
        }
      });
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
    }
  };

  // Set geographic bounds based on project location
  const setGeographicBounds = (autocomplete) => {
    const geocoder = new window.google.maps.Geocoder();
    const searchAddress = `${phaseCity}, ${phaseState} ${phaseZip}, USA`;
    
    geocoder.geocode({ address: searchAddress }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const bounds = new window.google.maps.LatLngBounds();
        
        const offset = 0.01; // ~1km radius
        bounds.extend(new window.google.maps.LatLng(
          location.lat() - offset, location.lng() - offset
        ));
        bounds.extend(new window.google.maps.LatLng(
          location.lat() + offset, location.lng() + offset
        ));
        
        autocomplete.setBounds(bounds);
      }
    });
  };

  const handlePlaceSelection = (place) => {
    const selectedAddress = getGoogleAddressFields(place);
    
    if (!selectedAddress.street) {
      console.warn('Selected address is missing street information:', selectedAddress, place.address_components);
      setValidationError('Please select a valid street address.');
      setIsValidAddress(false);
      return;
    }

    if (!selectedAddress.city || !selectedAddress.state) {
      console.warn('Selected address is missing required location information:', selectedAddress, place.address_components);
      setValidationError('Selected address is missing city, state, or zip.');
      setIsValidAddress(false);
      setSelectedAddress(selectedAddress);
      setSelectedStreet(selectedAddress.street);
      return;
    }

    // Validate that the selected address matches the project location
    const isValidLocation = 
      selectedAddress.city.toLowerCase() === phaseCity.toLowerCase() && 
      selectedAddress.state.toLowerCase() === phaseState.toLowerCase() && 
      selectedAddress.zip === phaseZip;

    if (isValidLocation) {
      setSelectedAddress(selectedAddress);
      setSelectedStreet(selectedAddress.street);
      setValidationError('');
      setIsValidAddress(true);
    } else {
      console.warn('Selected address does not match project location:', {...selectedAddress, projectCity: phaseCity, projectState: phaseState, phaseZip});
      setValidationError("The selected address does not match the city, state, or zip of the project.");
      setIsValidAddress(false);
      setSelectedAddress(selectedAddress);
      setSelectedStreet(selectedAddress.street);
    }
  };

  const handleCancel = () => {
    setSelectedStreet('');
    setValidationError('');
    setIsValidAddress(false);
    onHide();
  };

  const handleConfirm = () => {
    if (isValidAddress && selectedStreet) {
      onAddressSelect(selectedStreet, phaseIndex);
      onHide();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSelectedStreet(value);
    
    // Reset validation when user types manually
    if (!value.trim()) {
      setValidationError('Street address is required.');
      setIsValidAddress(false);
    } else {
      // If user types an address manually (not from Google Places), consider it valid
      setValidationError('');
      setIsValidAddress(true);
      // Clear any previously selected Google Places address since this is manual input
      setSelectedAddress(null);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleCancel}
      centered
      className="street-address-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Select Street Address</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="address-selection-container">
          {phaseCity && (phaseState || phaseZip) ? (
            <p className="modal-instructions">
              Select a street address in <strong>{phaseCity}, {phaseState} {phaseZip}</strong>.<span className="required-asterisk">*</span>
              <CustomTooltip
                style={{ backgroundColor: "white" }}
                title={"If this is a new address, please enter it manually."}
                placement="right"
                arrow
                disableFocusListener
                disableTouchListener
                interactive
              >
                <InfoIcon
                  style={{
                    marginBottom: "3px",
                    color: "#1c6fa3",
                    width: 20,
                    height: 18,
                    marginLeft: "5px"}}
                />
              </CustomTooltip>
            </p>
          ) : (
            <p className="modal-instructions">
              Select a street address for this phase.<span className="required-asterisk">*</span>
            </p>
          )}
          
          <div className="street-input-container">
            {/* <label htmlFor="street-address-input">
              Street Address<span className="required-asterisk">*</span>
            </label> */}
            <input
              id="street-address-input"
              ref={streetInputRef}
              type="text"
              className={`form-control street-address-input ${validationError ? 'is-invalid' : ''}`}
              placeholder="Select a street address..."
              value={selectedStreet}
              onChange={handleInputChange}
            />
            {validationError && (
              <div className="validation-error">
                {validationError}
              </div>
            )}
          </div>
          
          {isValidAddress && selectedAddress && (
            <div className="address-confirmation">
              ✓ Valid address selected: <strong>{selectedStreet}, {selectedAddress.city}, {selectedAddress.state} {selectedAddress.zip}</strong>
            </div>
          )}
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" 
          className="back-button"
          style={{width: "150px"}} 
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleConfirm}
          disabled={!isValidAddress || !selectedStreet}
          style={{width: "150px"}}
        >
          Use This Address
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StreetAddressModal;